import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/i18n";
import { Download, Loader2 } from "lucide-react";

const SIZES = ["1–50", "51–200", "201–1,000", "1,001–5,000", "5,000+"];

export function LeadCaptureModal({ open, onOpenChange, onSubmit, submitting }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", company_size: "" });
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("lead.required");
    if (!form.email.trim()) e.email = t("lead.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("lead.invalidEmail");
    if (!form.company.trim()) e.company = t("lead.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 sm:max-w-md" data-testid="lead-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{t("lead.title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("lead.desc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <Field label={t("lead.name")} error={errors.name}>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="bg-white border-slate-300 text-slate-900"
              placeholder={t("lead.namePh")}
              data-testid="lead-name-input"
            />
          </Field>
          <Field label={t("lead.email")} error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="bg-white border-slate-300 text-slate-900"
              placeholder={t("lead.emailPh")}
              data-testid="lead-email-input"
            />
          </Field>
          <Field label={t("lead.company")} error={errors.company}>
            <Input
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              className="bg-white border-slate-300 text-slate-900"
              placeholder={t("lead.companyPh")}
              data-testid="lead-company-input"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("lead.role")}>
              <Input
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
                className="bg-white border-slate-300 text-slate-900"
                placeholder={t("lead.rolePh")}
                data-testid="lead-role-input"
              />
            </Field>
            <Field label={t("lead.size")}>
              <Select value={form.company_size} onValueChange={(v) => set("company_size", v)}>
                <SelectTrigger
                  className="bg-white border-slate-300 text-slate-900"
                  data-testid="lead-size-select"
                >
                  <SelectValue placeholder={t("lead.sizePh")} />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300 text-slate-900">
                  {SIZES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s} {t("lead.employees")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white mt-2"
          data-testid="lead-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" /> {t("lead.saving")}
            </>
          ) : (
            <>
              <Download size={16} className="mr-2" /> {t("lead.submit")}
            </>
          )}
        </Button>
        <p className="text-[11px] text-slate-500 text-center">{t("lead.footnote")}</p>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-600 mb-1.5 block">{label}</Label>
      {children}
      {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}
    </div>
  );
}
